import { safeJsonParse } from '@/utils/parsing';

type Features = {
  [key: string]: boolean;
};

type FeatureManager<T extends Features> = {
  getFeatures: () => T;
  getFeature: <K extends keyof T>(feature: K, defaultValue?: boolean) => boolean;
}

type FeatureManagerConfig<T extends Features> = {
  initialValue: T
  localStoragePrefix?: string;
}

const createFeatureManager = <T extends Features>({ initialValue, localStoragePrefix = 'features.' }: FeatureManagerConfig<T>): FeatureManager<T> => {
  const defaultFeatures = { ...initialValue };
  const features = { ...initialValue };

  const getFeature = <K extends keyof T>(feature: K, defaultValue = false): boolean => {
    const localStorageValue = window.localStorage.getItem(`${localStoragePrefix}${feature as string}`);
    features[feature] = localStorageValue ? JSON.parse(localStorageValue) : defaultFeatures[feature];
    return features[feature] ?? defaultValue;
  };

  const getFeatures = () => {
    return Object.keys(features).reduce((previousValue, key) => {
      return {
        ...previousValue,
        [key]: getFeature(key)
      };
    }, {} as T);
  };

  return {
    getFeatures,
    getFeature
  };
};

const rf = () => (typeof window !== 'undefined' ? window.__config__?.FEATURES : undefined) ?? {};

const FEATURES = {
  clientViewStepTherapy: rf().clientViewStepTherapy ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_CLIENT_VIEW_STEP_THERAPY) ?? false,
  clientStateReports: rf().clientStateReports ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_CLIENT_STATE_REPORTS) ?? false,
  draftCoverageWorkflow: rf().draftCoverageWorkflow ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_DRAFT_COVERAGE_WORKFLOW) ?? false,
  coverageNeedsReview: rf().coverageNeedsReview ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_COVERAGE_NEEDS_REVIEW) ?? false,
  marketShareReport: rf().marketShareReport ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_MARKET_SHARE_REPORT) ?? false,
  drugCalendarEnhancements: rf().drugCalendarEnhancements ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_DRUG_CALENDAR_ENHANCEMENTS) ?? false,
  receiveInMobile: rf().receiveInMobile ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_RECEIVE_IN_MOBILE) ?? false,
  draftCoverageImporter: rf().draftCoverageImporter ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_DRAFT_COVERAGE_IMPORTER) ?? false,
  trackedDrugMbRemoval: rf().trackedDrugMbRemoval ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_TRACKED_DRUG_MB_REMOVAL) ?? false,
  userGeoData: rf().userGeoData ?? safeJsonParse<boolean>(import.meta.env.VITE_FEATURES_USER_GEO_DATA) ?? false,
} as const;

export const featureManager = createFeatureManager({ initialValue: FEATURES });
